/**
 * @file Radius scale for raw `style` props (SE-8552).
 * @description The closed radius scale as numbers, for the places a class
 * cannot reach: `borderRadius` in a style object, an Animated style, a
 * native-only picker. Values are DERIVED from tailwind.config.js
 * `theme.borderRadius`, so `RADIUS.md` and `rounded-md` can never drift apart.
 *
 * Role radii (D9): chips `full` · buttons `sm` · cards `md` · modals/dialogs
 * `lg` · sheets `xl` · inputs `sm`. The `design-system-radius/no-literal-radius`
 * lint rule (eslint.radius-literal.ts) rejects numeric radius literals in app
 * code, so a style prop reads one of these instead.
 * @module config/radius
 */
import tailwindConfig from "../../tailwind.config.js";

/** The closed radius rungs, in tailwind.config.js order. */
export type RadiusKey = "none" | "sm" | "md" | "lg" | "xl" | "full";

const RADIUS_KEYS: readonly RadiusKey[] = [
  "none",
  "sm",
  "md",
  "lg",
  "xl",
  "full",
];

const SCALE = (
  tailwindConfig as { theme: { borderRadius: Record<string, string> } }
).theme.borderRadius;

/**
 * Parses one `"12px"` rung to its number.
 * @param key - The rung to read.
 * @returns The rung in px.
 */
const toPx = (key: RadiusKey): number => {
  const value = SCALE[key];
  const px = value === undefined ? Number.NaN : Number.parseFloat(value);
  if (!Number.isFinite(px)) {
    throw new TypeError(
      `config/radius: tailwind.config.js theme.borderRadius.${key} is missing or not a px value — the radius scale and RADIUS have drifted (SE-8552).`
    );
  }
  return px;
};

/** The closed radius scale in px, for style props (SE-8552). */
export const RADIUS: Readonly<Record<RadiusKey, number>> = Object.freeze(
  Object.fromEntries(RADIUS_KEYS.map(key => [key, toPx(key)])) as Record<
    RadiusKey,
    number
  >
);
